import './settings-leaf.css';
import { useTranslation } from 'react-i18next';
import { TextInput } from '../ui.tsx';
import { crossesMidnight, parseWorkDays } from '@shared/scheduling-hours.ts';
import { CardSaveFooter, useCompanyCardForm } from './company-card-form.tsx';
import type { Company } from '@shared/types/index.ts';

/**
 * When the company takes bookings. Its own card, not a subsection of the
 * reminders one where it first landed: these hours drive the booking grid and
 * the quick-pick suggestions — scheduling machinery — and had nothing to do
 * with the reminder emails they were filed under.
 */

const snapshot = (company: Company) => ({
  workDayStartLocal: company.workDayStartLocal,
  workDayEndLocal: company.workDayEndLocal,
  workDays: company.workDays,
});

const toPayload = (draft: ReturnType<typeof snapshot>) => ({
  workDayStartLocal: draft.workDayStartLocal,
  workDayEndLocal: draft.workDayEndLocal,
  workDays: parseWorkDays(draft.workDays),
});

export default function WorkingHoursCard() {
  const { t } = useTranslation();
  const form = useCompanyCardForm(snapshot, toPayload);

  const draft = form.draft;
  if (!draft) return null;

  const overnight = crossesMidnight({ startLocal: draft.workDayStartLocal, endLocal: draft.workDayEndLocal });

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="working-hours-heading">
      <div className="work-section-heading">
        <h2 id="working-hours-heading" className="mb-4 text-lg font-semibold">
          {t('settings.workingHoursHeading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.workingHoursDescription')}</p>

      <div className="grid gap-x-4 sm:grid-cols-2">
        <TextInput
          label={t('settings.workDayStart')}
          type="time"
          step={300}
          value={draft.workDayStartLocal}
          disabled={form.readOnly}
          error={form.fieldErrors.workDayStartLocal}
          onChange={(event) => form.set('workDayStartLocal', event.target.value)}
        />
        <TextInput
          label={t('settings.workDayEnd')}
          type="time"
          step={300}
          value={draft.workDayEndLocal}
          disabled={form.readOnly}
          error={form.fieldErrors.workDayEndLocal}
          onChange={(event) => form.set('workDayEndLocal', event.target.value)}
        />
      </div>
      {/*
        An end earlier than the start is a night shift, not a mistake, and it
        says so the moment the pair is typed rather than after a save. The
        note also answers the question the working-day chips raise next: a
        Friday night crew works Friday, and finishes on Saturday morning
        without Saturday being one of their days.
      */}
      {overnight ? <p className="field-hint mb-3">{t('settings.workDayOvernightHint')}</p> : null}

      <fieldset className="mb-3">
        <legend className="field-label">{t('settings.workDaysLabel')}</legend>
        <div className="flex flex-wrap gap-2">
          {[0, 1, 2, 3, 4, 5, 6].map((day) => {
            const active = parseWorkDays(draft.workDays).includes(day);
            return (
              <button
                key={day}
                type="button"
                role="checkbox"
                aria-checked={active}
                disabled={form.readOnly}
                onClick={() => {
                  const current = parseWorkDays(draft.workDays);
                  const next = active ? current.filter((d) => d !== day) : [...current, day].sort();
                  if (next.length > 0) form.set('workDays', next.join(','));
                }}
                className={
                  'rounded-full border px-3 py-1.5 text-sm font-medium disabled:opacity-60 ' +
                  (active
                    ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
                    : 'border-[color:var(--color-line)] text-[color:var(--color-muted)]')
                }
              >
                {t(`schedule.dayOfWeek.${day}`)}
              </button>
            );
          })}
        </div>
        <p className="field-hint mt-2">{t('settings.workDaysHint')}</p>
      </fieldset>

      <CardSaveFooter form={form} saveLabel={t('settings.saveHours')} />
    </section>
  );
}
